import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Slider from "@mui/material/Slider";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  ComposableMap,
  Geographies,
  Geography,
  Line,
  Marker,
  ZoomableGroup,
} from "react-simple-maps";

const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";
const africaCenter = [22, 8];
const teamCountries = new Set(["United Kingdom", "Italy", "United States of America"]);
const lacorCoordinates = [32.2531, 2.7675];
const teamRoutes = [
  { id: "uk-to-lacor", from: [-0.1276, 51.5072] },
  { id: "italy-to-lacor", from: [12.4964, 41.9028] },
  { id: "usa-to-lacor", from: [-74.006, 40.7128] },
];

const PartnerMap = ({ partners }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const minZoom = isSmallScreen ? 3.7 : 1.15;
  const maxZoom = isSmallScreen ? 8 : 5.4;
  const initialZoom = isSmallScreen ? 4.2 : 1.65;
  const markerScale = isSmallScreen ? 3.4 : 1;
  const routeStrokeWidth = isSmallScreen ? 4 : 1.15;
  const navigate = useNavigate();
  const [zoom, setZoom] = useState(initialZoom);
  const [mapCenter, setMapCenter] = useState(africaCenter);
  const scale = useMemo(() => 200 * zoom, [zoom]);

  useEffect(() => {
    setZoom(initialZoom);
    setMapCenter(africaCenter);
  }, [initialZoom]);

  const updateZoom = (nextZoom) => {
    const clampedZoom = Math.min(maxZoom, Math.max(minZoom, nextZoom));

    if (clampedZoom !== zoom) {
      setMapCenter(africaCenter);
    }

    setZoom(clampedZoom);
  };

  return (
    <Box
      sx={{
        width: "100%",
        overflow: "hidden",
        position: "relative",
        height: { xs: "calc(100vh - 72px)", md: "calc(100vh - 82px)" },
        minHeight: { xs: 430, md: 620 },
        bgcolor: "background.default",
        "& .partner-tooltip": {
          opacity: 0,
          transform: "translateY(6px)",
          transition: "opacity 160ms ease, transform 160ms ease",
          pointerEvents: "none",
        },
        "& g[role='button']:hover .partner-tooltip, & g[role='button']:focus .partner-tooltip":
          {
            opacity: 1,
            transform: "translateY(0)",
          },
        "& g[role='button']": {
          WebkitTapHighlightColor: "transparent",
          outline: "none",
        },
        "& .partner-route-burst": {
          animation: "partnerRouteBurst 3.2s linear infinite",
        },
        "@keyframes partnerRouteBurst": {
          "0%": {
            clipPath: "inset(0 100% 0 0)",
            opacity: 0,
          },
          "10%": {
            clipPath: "inset(0 100% 0 0)",
            opacity: 0.82,
          },
          "72%": {
            clipPath: "inset(0 0 0 0)",
            opacity: 0.82,
          },
          "84%": {
            clipPath: "inset(0 0 0 0)",
            opacity: 0,
          },
          "100%": {
            clipPath: "inset(0 100% 0 0)",
            opacity: 0,
          },
        },
      }}
    >
      <Box sx={{ position: "absolute", inset: 0 }}>
        <ComposableMap
          projection="geoMercator"
          projectionConfig={{
            center: africaCenter,
            scale,
          }}
          width={1440}
          height={760}
          style={{ width: "100%", height: "100%" }}
        >
          <defs>
            <filter
              id="lacor-marker-shadow"
              x="-80%"
              y="-80%"
              width="260%"
              height="260%"
            >
              <feDropShadow
                dx="0"
                dy="4"
                stdDeviation="4"
                floodColor="rgba(6,57,112,0.32)"
              />
            </filter>
            <filter
              id="lacor-marker-glow"
              x="-120%"
              y="-120%"
              width="340%"
              height="340%"
            >
              <feGaussianBlur stdDeviation="2.5" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          <ZoomableGroup
            center={mapCenter}
            zoom={1}
            minZoom={1}
            maxZoom={1}
            onMoveEnd={({ coordinates }) => {
              setMapCenter(coordinates);
            }}
            filterZoomEvent={(event) => {
              if (event.type === "wheel" || event.type === "dblclick") {
                return false;
              }

              if (event.type === "touchstart") {
                return event.touches && event.touches.length > 1;
              }

              return true;
            }}
          >
            <Geographies geography={geoUrl}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const isUganda = geo.properties.name === "Uganda";
                  const isTeamCountry = teamCountries.has(geo.properties.name);

                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={
                        isUganda
                          ? theme.palette.primary.main
                          : isTeamCountry
                          ? "rgba(6, 57, 112, 0.22)"
                          : "rgba(6, 57, 112, 0.08)"
                      }
                      stroke={
                        isUganda
                          ? theme.palette.common.white
                          : "rgba(6, 57, 112, 0.28)"
                      }
                      strokeWidth={isUganda ? 1.15 : 0.7}
                      style={{
                        default: { outline: "none" },
                        hover: {
                          fill: isUganda
                            ? theme.palette.primary.main
                            : isTeamCountry
                            ? "rgba(6, 57, 112, 0.3)"
                            : "rgba(6, 57, 112, 0.14)",
                          outline: "none",
                        },
                        pressed: { outline: "none" },
                      }}
                    />
                  );
                })
              }
            </Geographies>
            {teamRoutes.map((route) => (
              <g key={route.id} style={{ pointerEvents: "none" }}>
                <Line
                  from={route.from}
                  to={lacorCoordinates}
                  stroke="rgba(91, 126, 162, 0.62)"
                  strokeWidth={routeStrokeWidth}
                  strokeLinecap="round"
                  strokeDasharray={isSmallScreen ? "12 10" : "8 7"}
                  className="partner-route-burst"
                  fill="none"
                />
              </g>
            ))}
            {partners.map((partner) => (
              <Marker key={partner.id} coordinates={partner.coordinates}>
                <g
                  onClick={() => navigate(partner.path)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      navigate(partner.path);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`Open ${partner.name}`}
                  style={{
                    cursor: "pointer",
                    WebkitTapHighlightColor: "transparent",
                    outline: "none",
                  }}
                >
                  <g transform={`scale(${markerScale})`}>
                    <circle
                      r={isSmallScreen ? 18 : 12}
                      fill="transparent"
                    />
                    <g className="partner-tooltip">
                      <path
                        d="M -8 -29 L 0 -18 L 8 -29 Z"
                        fill={theme.palette.primary.main}
                      />
                      <rect
                        x={-98}
                        y={-64}
                        width={196}
                        height={36}
                        rx={8}
                        fill={theme.palette.primary.main}
                      />
                      <text
                        textAnchor="middle"
                        y={-41}
                        style={{
                          fontFamily: "Inter, sans-serif",
                          fill: theme.palette.common.white,
                          fontSize: 13,
                          fontWeight: 800,
                        }}
                      >
                        {partner.name}
                      </text>
                    </g>
                    <circle
                      fill="none"
                      stroke={theme.palette.donate.primary}
                      strokeWidth={1.5}
                      opacity={0.42}
                    >
                      <animate
                        attributeName="r"
                        values="9;22"
                        dur="2.4s"
                        repeatCount="indefinite"
                      />
                      <animate
                        attributeName="opacity"
                        values="0.42;0"
                        dur="2.4s"
                        repeatCount="indefinite"
                      />
                    </circle>
                    <circle
                      r={7}
                      fill={theme.palette.donate.primary}
                      stroke={theme.palette.common.white}
                      strokeWidth={2}
                      filter="url(#lacor-marker-shadow)"
                    />
                  </g>
                </g>
              </Marker>
            ))}
          </ZoomableGroup>
        </ComposableMap>
      </Box>
      <Box
        sx={{
          position: "absolute",
          right: { xs: 12, sm: 24 },
          bottom: { xs: 12, sm: 24 },
          width: { xs: 180, sm: 230 },
          display: "flex",
          alignItems: "center",
          gap: 1,
          bgcolor: "rgba(255,255,255,0.86)",
          border: "1px solid rgba(6, 57, 112, 0.12)",
          boxShadow: "0 12px 34px rgba(6, 57, 112, 0.12)",
          borderRadius: 999,
          px: 1,
          py: 0.5,
          backdropFilter: "blur(12px)",
        }}
      >
        <IconButton
          aria-label="Zoom out"
          size="small"
          onClick={() => updateZoom(zoom - 0.1)}
          sx={{ color: theme.palette.primary.main }}
        >
          <RemoveIcon fontSize="small" />
        </IconButton>
        <Slider
          aria-label="Map zoom"
          min={minZoom}
          max={maxZoom}
          step={0.05}
          value={zoom}
          onChange={(event, value) => updateZoom(value)}
          sx={{
            color: theme.palette.primary.main,
            "& .MuiSlider-thumb": {
              width: 16,
              height: 16,
            },
          }}
        />
        <IconButton
          aria-label="Zoom in"
          size="small"
          onClick={() => updateZoom(zoom + 0.1)}
          sx={{ color: theme.palette.primary.main }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

export default PartnerMap;
